import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import Chart from "react-apexcharts";
import { connect } from "react-redux";
import { useHistory, withRouter } from "react-router-dom";
import dayjs from "dayjs";
import jsPDF from "jspdf";

import {
    Badge, Card, CardBody, CardHeader,
    Col, Container, Progress, Row,
} from "reactstrap";

import { TimelineCellRenderer } from "./TimelineCellRenderer";

import { getAreas, getUserGuilds } from "../store/goals/actions";

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTHS_TICK  = ["J","F","M","A","M","J","J","A","S","O","N","D"];
const SEASONS      = ["Spring","Summer","Fall","Winter"];

const NUTRIENT_STYLES = {
    "Heavy Feeder": { background: "#c8e6c9", color: "#2e7d32" },
    "Light Feeder":  { background: "#bbdefb", color: "#1565c0" },
    "Heavy Giver":   { background: "#fff9c4", color: "#f57f17" },
};

const ACTION_COLORS = {
    "Seed Start": "#00b894",
    "Transplant": "#008FFB",
    "Harvest":    "#FEB019",
};

// ─── Shared small components ──────────────────────────────────────────────────

const WidgetCard = ({ title, children, headerAction }) => (
    <Card className="h-100" style={{ marginBottom: 0 }}>
        <CardHeader style={{
            backgroundColor: "#BEC8B6", fontWeight: 600,
            padding: "8px 12px", fontSize: 13,
            display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
            <span>{title}</span>
            {headerAction && <span>{headerAction}</span>}
        </CardHeader>
        <CardBody style={{ padding: "10px 12px" }}>
            {children}
        </CardBody>
    </Card>
);

const StatCard = ({ title, value, sub1, sub2, linkText, onLinkClick, accent }) => (
    <Card style={{ borderTop: `3px solid ${accent}`, marginBottom: 0 }}>
        <CardBody style={{ padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#6f7a72", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {title}
                </div>
                {linkText && (
                    <span onClick={onLinkClick} style={{ fontSize: 11, color: "#FF9900", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {linkText}
                    </span>
                )}
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#2f3a34", lineHeight: 1, marginBottom: 8 }}>{value}</div>
            {sub1 && <div style={{ fontSize: 12, color: "#6f7a72" }}>{sub1}</div>}
            {sub2 && <div style={{ fontSize: 11, color: "#4a5e44", marginTop: 4, fontWeight: 500 }}>{sub2}</div>}
        </CardBody>
    </Card>
);

// ─── Home component ───────────────────────────────────────────────────────────

const Home = (props) => {
    const history = useHistory();

    const {
        profile,
        userareas, userGuilds,
        onGetAreas, onGetUserGuilds,
    } = props;

    // ── Safe arrays ───────────────────────────────────────────────────────────

    const safeAreas  = useMemo(() => Array.isArray(userareas)      ? userareas      : [], [userareas]);
    const safeGuilds = useMemo(() => Array.isArray(userGuilds)      ? userGuilds      : [], [userGuilds]);

    const [unitmeasurement, setUnitmeasurement] = useState("imperial");

    // ── Data fetching ─────────────────────────────────────────────────────────

    // PrivateRoute resolves the session and loads the profile before this page
    // renders, so there is no session state left to wait on here.
    useEffect(() => {
        onGetAreas();
        onGetUserGuilds();
    }, []);

    useEffect(() => {
        const um = profile?.unitmeasurement;
        if (typeof um === "string" && um.length > 0) setUnitmeasurement(um);
    }, [profile]);

    // ── Derived data ──────────────────────────────────────────────────────────

    // DynamoDB sometimes returns numeric fields as {N: "0.8125"} instead of plain numbers
    const toNum = (v) => {
        if (v == null) return 0;
        if (typeof v === "number") return v;
        if (typeof v === "object" && v.N !== undefined) return parseFloat(v.N) || 0;
        return parseFloat(v) || 0;
    };
    // Unwrap DynamoDB List {L:[...]} or plain array
    const getList = (v) => Array.isArray(v) ? v : (v?.L || []);
    // Unwrap DynamoDB Map {M:{...}} or plain object
    const getMap  = (v) => (v != null && v.M != null) ? v.M : (v || {});

    const currYear = new Date().getFullYear();

    const activeAreas = useMemo(
        () => safeAreas.filter(a => a.name?.toLowerCase() !== "unassigned" && Number(a.year) === currYear),
        [safeAreas]
    );

    // Row 1 — stat cards

    const totalPlants = useMemo(
        () => activeAreas.reduce((sum, area) =>
            sum + (area.plants || []).reduce((s, p) => s + (toNum(p.numPlants) || 1), 0), 0),
        [activeAreas]
    );

    // Plants seeded or transplanted this month (momentum indicator)
    const plantsThisMonth = useMemo(() => {
        const now = dayjs();
        return activeAreas.reduce((sum, area) =>
            sum + (area.plants || []).filter(p => {
                const d = dayjs(p.transplantDate || p.seedStartDate);
                return d.isValid() && d.month() === now.month() && d.year() === now.year();
            }).reduce((s, p) => s + (toNum(p.numPlants) || 1), 0), 0);
    }, [activeAreas]);

    // Harvest — actual in oz, estimated in lbs (estHarvest = yield_oz * count / 16)
    const totalHarvestOz = useMemo(() => {
        let t = 0;
        activeAreas.forEach(a =>
            (a.plants || []).forEach(p =>
                getList(p.harvests).forEach(hRaw => { t += toNum(getMap(hRaw).amount); })
            )
        );
        return t;
    }, [activeAreas]);

    const totalEstHarvestLbs = useMemo(
        () => activeAreas.reduce((sum, a) =>
            sum + (a.plants || []).reduce((s, p) => s + toNum(p.estHarvest), 0), 0),
        [activeAreas]
    );

    const harvestUnit = unitmeasurement === "imperial" ? "lbs" : "kg";
    const lbsToDisplay = (lbs) => unitmeasurement === "imperial"
        ? parseFloat(lbs.toFixed(1))
        : parseFloat((lbs * 0.453592).toFixed(2));
    const totalHarvestValue = unitmeasurement === "imperial"
        ? (totalHarvestOz / 16).toFixed(1)
        : (totalHarvestOz * 0.0283495).toFixed(2);
    const totalEstValue = unitmeasurement === "imperial"
        ? totalEstHarvestLbs.toFixed(1)
        : (totalEstHarvestLbs * 0.4536).toFixed(1);
    const harvestPct = totalEstHarvestLbs > 0
        ? Math.round((totalHarvestOz / 16) / totalEstHarvestLbs * 100)
        : 0;

    // Space — exclude Complete/Failed plants; Unassigned area already excluded by activeAreas
    const totalSpaceUsed = useMemo(
        () => activeAreas.reduce((sum, a) =>
            sum + (a.plants || [])
                .filter(p => p.status !== "Complete" && p.status !== "Failed")
                .reduce((s, p) => s + toNum(p.spaceUsed), 0), 0),
        [activeAreas]
    );

    const totalAreaSpace = useMemo(
        () => activeAreas.reduce((sum, a) => sum + toNum(a.info?.totalSpace ?? a.space), 0),
        [activeAreas]
    );

    const spaceUnit = unitmeasurement === "imperial" ? "ft²" : "m²";
    const toSpace = (ft2) => unitmeasurement === "imperial"
        ? `${ft2.toFixed(1)} ${spaceUnit}`
        : `${(ft2 / 10.764).toFixed(1)} ${spaceUnit}`;
    const spaceAvailable = Math.max(0, totalAreaSpace - totalSpaceUsed);

    // Guilds summary
    const guildNameList = safeGuilds.slice(0, 3).map(g => g.guildName).join(" · ");
    const totalGuildPlants = safeGuilds.reduce(
        (sum, g) => sum + [g.starPlayer, ...(g.guildPlants || [])].filter(Boolean).length, 0
    );
    const guildCountLabel = safeGuilds.length === 1 ? "it"
        : safeGuilds.length === 2 ? "both"
        : `all ${safeGuilds.length}`;

    // Row 2 — next action per annual plant in next 60 days
    const upcomingPlantActions = useMemo(() => {
        const today = dayjs();
        const results = [];
        activeAreas.forEach(area => {
            (area.plants || [])
                .filter(p => p.season?.toLowerCase() !== "perennial")
                .forEach(plant => {
                    const steps = [
                        { action: "Seed Start", date: plant.seedStartDate },
                        { action: "Transplant", date: plant.transplantDate },
                        { action: "Harvest",    date: plant.harvestDate },
                    ];
                    for (const { action, date } of steps) {
                        if (!date) continue;
                        const d = dayjs(date);
                        if (!d.isValid()) continue;
                        const diff = d.diff(today, "day");
                        if (diff >= 0 && diff <= 60) {
                            results.push({
                                species:   plant.species || plant.name || "Unknown",
                                variety:   plant.variety || "",
                                areaName:  area.name,
                                action,
                                dateLabel: d.format("MMM D"),
                                diff,
                            });
                            break;
                        }
                    }
                });
        });
        return results.sort((a, b) => a.diff - b.diff).slice(0, 10);
    }, [activeAreas]);


    // Season rotations (case-insensitive match on season_rotation)
    const seasonRotations = useMemo(() =>
        activeAreas
            .map(area => {
                const bySeason = { Spring: [], Summer: [], Fall: [], Winter: [] };
                (area.plants || []).forEach(p => {
                    const raw = p.season_rotation || "";
                    const key = SEASONS.find(s => s.toLowerCase() === raw.toLowerCase());
                    if (key) bySeason[key].push(p);
                });
                const hasAny = SEASONS.some(s => bySeason[s].length > 0);
                return hasAny ? { areaName: area.name, bySeason } : null;
            })
            .filter(Boolean),
        [activeAreas]
    );

    // Growing Now widget
    const currentSeason = useMemo(() => {
        const m = new Date().getMonth();
        if (m >= 2 && m <= 4) return "Spring";
        if (m >= 5 && m <= 7) return "Summer";
        if (m >= 8 && m <= 10) return "Fall";
        return "Winter";
    }, []);

    const topGrowingPlants = useMemo(() => {
        const bySpecies = {};
        activeAreas.forEach(area => {
            (area.plants || []).forEach(p => {
                const key = p.species || p.name || "Unknown";
                if (!bySpecies[key]) bySpecies[key] = { species: key, bedSet: new Set(), count: 0 };
                bySpecies[key].bedSet.add(area.id);
                bySpecies[key].count += toNum(p.numPlants) || 1;
            });
        });
        return Object.values(bySpecies)
            .map(({ species, bedSet, count }) => ({ species, beds: bedSet.size, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
    }, [activeAreas]);

    // Row 4 — timeline plants (annual, all three dates present, deduplicated)
    const timelinePlants = useMemo(() => {
        const seen = new Set();
        const result = [];
        activeAreas.forEach(area =>
            (area.plants || [])
                .filter(p => p.seedStartDate && p.transplantDate && p.harvestDate
                          && p.season?.toLowerCase() !== "perennial")
                .forEach(plant => {
                    const key = `${plant.species}-${plant.variety}-${plant.seedStartDate}-${plant.harvestDate}`;
                    if (!seen.has(key)) {
                        seen.add(key);
                        result.push(plant);
                    }
                })
        );
        return result.sort((a, b) =>
            dayjs(a.seedStartDate, ["M/D/YYYY", "YYYY-MM-DD"]).valueOf() -
            dayjs(b.seedStartDate, ["M/D/YYYY", "YYYY-MM-DD"]).valueOf()
        );
    }, [activeAreas]);

    // Row 4 — harvest by area + monthly totals
    const harvestByArea = useMemo(() =>
        activeAreas.map(area => {
            let actual = 0;
            const monthly = Array(12).fill(0);
            (area.plants || []).forEach(p =>
                getList(p.harvests).forEach(hRaw => {
                    const h = getMap(hRaw);
                    const amt = toNum(h.amount);
                    actual += amt;
                    const dateStr = typeof h.date === "string" ? h.date : (h.date?.S || "");
                    const m = new Date(dateStr).getMonth();
                    if (!isNaN(m)) monthly[m] += amt;
                })
            );
            const estimated = (area.plants || []).reduce(
                (s, p) => s + toNum(p.estHarvest), 0
            );
            const actualLbs = actual / 16;
            return {
                name: area.name,
                actual: parseFloat(actualLbs.toFixed(1)),
                estimated: parseFloat(estimated.toFixed(1)),
                pct: estimated > 0 ? Math.min(100, Math.round(actualLbs / estimated * 100)) : 0,
                monthly: monthly.map(oz => parseFloat((oz / 16).toFixed(2))),
            };
        }).filter(a => a.estimated > 0 || a.actual > 0),
        [activeAreas]
    );

    const monthlyHarvestTotals = useMemo(() => {
        const totals = Array(12).fill(0);
        activeAreas.forEach(a =>
            (a.plants || []).forEach(p =>
                getList(p.harvests).forEach(hRaw => {
                    const h = getMap(hRaw);
                    const dateStr = typeof h.date === "string" ? h.date : (h.date?.S || "");
                    const m = new Date(dateStr).getMonth();
                    if (!isNaN(m)) totals[m] += toNum(h.amount);
                })
            )
        );
        const lbs = totals.map(oz => oz / 16);
        return unitmeasurement === "imperial"
            ? lbs.map(v => parseFloat(v.toFixed(2)))
            : lbs.map(v => parseFloat((v * 0.453592).toFixed(2)));
    }, [activeAreas, unitmeasurement]);

    // Today line position in the timeline widget (30% name col + 70% bar col)
    const yearStart  = dayjs(`${currYear}-01-01`);
    const totalDays  = dayjs(`${currYear}-12-31`).diff(yearStart, "day");
    const todayPct   = (dayjs().diff(yearStart, "day") / totalDays) * 100;
    const todayLeft  = `${(30 + todayPct * 0.7).toFixed(1)}%`;

    // ── PDF export ───────────────────────────────────────────────────────────

    const printTimeline = () => {
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

        const pageW    = 297;
        const pageH    = 210;
        const margin   = 15;
        const cw       = pageW - margin * 2;   // usable content width
        const namePct  = 0.28;
        const barPct   = 0.72;
        const nameW    = cw * namePct;
        const barW     = cw * barPct;
        const barX     = margin + nameW;
        const rowH     = 7;
        const monthW   = barW / 12;

        const pctOf = (d) => Math.max(0, Math.min(1, d.diff(yearStart, "day") / totalDays));

        // ── Title
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.setTextColor(47, 58, 52);
        doc.text(`Plant Timeline — ${currYear}`, margin, margin + 5);

        let y = margin + 13;

        // ── Month headers
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);
        doc.setTextColor(150, 150, 150);
        MONTHS_SHORT.forEach((m, i) => {
            doc.text(m, barX + i * monthW + monthW / 2, y, { align: "center" });
        });
        y += 4;

        const rowsPerPage = Math.floor((pageH - y - margin) / rowH);

        // ── Vertical month grid lines (drawn once per page section)
        const drawGrid = (startY, count) => {
            doc.setDrawColor(220, 220, 220);
            doc.setLineWidth(0.2);
            for (let i = 0; i <= 12; i++) {
                const x = barX + i * monthW;
                doc.line(x, startY, x, startY + count * rowH);
            }
            // Today line
            const tx = barX + pctOf(dayjs()) * barW;
            doc.setDrawColor(255, 69, 96);
            doc.setLineWidth(0.5);
            doc.line(tx, startY, tx, startY + count * rowH);
            doc.setLineWidth(0.2);
        };

        // ── Rows
        let pageStart = y;
        let pageRowCount = 0;

        timelinePlants.forEach((plant, i) => {
            // Page break
            if (i > 0 && i % rowsPerPage === 0) {
                drawGrid(pageStart, pageRowCount);
                doc.addPage();
                y = margin + 5;
                pageStart = y;
                pageRowCount = 0;
                // Redraw month headers on new page
                doc.setFont("helvetica", "normal");
                doc.setFontSize(7);
                doc.setTextColor(150, 150, 150);
                MONTHS_SHORT.forEach((m, idx) => {
                    doc.text(m, barX + idx * monthW + monthW / 2, y, { align: "center" });
                });
                y += 4;
                pageStart = y;
            }

            const rowY = pageStart + pageRowCount * rowH;

            // Alternating row bg
            if (pageRowCount % 2 === 0) {
                doc.setFillColor(248, 251, 248);
                doc.rect(margin, rowY, cw, rowH, "F");
            }

            // Plant name
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7);
            doc.setTextColor(47, 58, 52);
            const label = `${plant.species}${plant.variety ? ` (${plant.variety})` : ""}`;
            doc.text(label, margin, rowY + rowH * 0.68, { maxWidth: nameW - 2 });

            // Background grey bar
            doc.setFillColor(224, 224, 224);
            doc.rect(barX, rowY + rowH * 0.28, barW, rowH * 0.44, "F");

            const s = dayjs(plant.seedStartDate, ["M/D/YYYY", "YYYY-MM-DD"]);
            const m = dayjs(plant.transplantDate, ["M/D/YYYY", "YYYY-MM-DD"]);
            const e = dayjs(plant.harvestDate,    ["M/D/YYYY", "YYYY-MM-DD"]);

            if (s.isValid() && m.isValid() && e.isValid()) {
                const sp = pctOf(s), mp = pctOf(m), ep = pctOf(e);

                // Green: seed → transplant
                doc.setFillColor(0, 184, 148);
                doc.rect(barX + sp * barW, rowY + rowH * 0.28, (mp - sp) * barW, rowH * 0.44, "F");

                // Blue: transplant → harvest
                doc.setFillColor(9, 132, 227);
                doc.rect(barX + mp * barW, rowY + rowH * 0.28, (ep - mp) * barW, rowH * 0.44, "F");
            }

            pageRowCount++;
        });

        drawGrid(pageStart, pageRowCount);

        // ── Legend
        const legY = pageStart + pageRowCount * rowH + 7;
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);

        doc.setFillColor(0, 184, 148);
        doc.rect(margin, legY, 7, 3.5, "F");
        doc.text("Seed → Transplant", margin + 9, legY + 3);

        doc.setFillColor(9, 132, 227);
        doc.rect(margin + 52, legY, 7, 3.5, "F");
        doc.text("Transplant → Harvest", margin + 61, legY + 3);

        doc.setDrawColor(255, 69, 96);
        doc.setLineWidth(0.6);
        doc.line(margin + 118, legY + 1.75, margin + 125, legY + 1.75);
        doc.setLineWidth(0.2);
        doc.text("Today", margin + 127, legY + 3);

        doc.save(`plant-timeline-${currYear}.pdf`);
    };

    // ── Dashboard ─────────────────────────────────────────────────────────────

    return (
        <div className="page-content">
            <Container fluid>

                {/* ── Row 1: Stat cards ────────────────────────────────────── */}
                <Row className="mb-3 g-3">
                    <Col md={3}>
                        <Card style={{ borderTop: "3px solid #00E396", marginBottom: 0 }} className="h-100">
                            <CardBody style={{ padding: "14px 16px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: "#6f7a72", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                        Growing Now
                                    </div>
                                    <span onClick={() => history.push("/plants/inventory")}
                                          style={{ fontSize: 11, color: "#FF9900", cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
                                        View all →
                                    </span>
                                </div>
                                <div style={{ fontSize: 11, color: "#6f7a72", marginBottom: 8 }}>
                                    {currentSeason} {currYear}
                                </div>
                                {topGrowingPlants.length === 0 ? (
                                    <p className="text-muted small mb-0">No plants added yet.</p>
                                ) : (
                                    topGrowingPlants.map((p, i) => (
                                        <div key={i} style={{
                                            display: "flex", justifyContent: "space-between",
                                            alignItems: "center", padding: "4px 0",
                                            borderBottom: i < topGrowingPlants.length - 1 ? "1px solid #f0f0f0" : "none",
                                        }}>
                                            <span style={{ fontSize: 12, fontWeight: 500, color: "#2f3a34" }}>{p.species}</span>
                                            <div style={{ fontSize: 11, color: "#6f7a72", textAlign: "right" }}>
                                                <span>{p.beds} bed{p.beds !== 1 ? "s" : ""}</span>
                                                <span style={{ marginLeft: 6 }}>{p.count} plants</span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <StatCard
                            title="Total Harvest"
                            value={`${totalHarvestValue} ${harvestUnit}`}
                            sub1={`of ${totalEstValue} ${harvestUnit} expected`}
                            sub2={totalEstHarvestLbs > 0 ? `${harvestPct}% of season goal` : null}
                            linkText="Log →"
                            onLinkClick={() => history.push("/plants/log-harvest")}
                            accent="#FEB019"
                        />
                    </Col>
                    <Col md={3}>
                        <StatCard
                            title="Space Used"
                            value={toSpace(totalSpaceUsed)}
                            sub1={totalAreaSpace > 0 ? `of ${toSpace(totalAreaSpace)} total` : "planted area"}
                            sub2={totalAreaSpace > 0 ? `${toSpace(spaceAvailable)} available` : null}
                            linkText="Add plant →"
                            onLinkClick={() => history.push("/plants/inventory", { openAddPlant: true })}
                            accent="#008FFB"
                        />
                    </Col>
                    <Col md={3}>
                        <StatCard
                            title="Active Guilds"
                            value={safeGuilds.length}
                            sub1={guildNameList || "No guilds yet"}
                            sub2={totalGuildPlants > 0 ? `${totalGuildPlants} plants across ${guildCountLabel}` : null}
                            linkText="Build →"
                            onLinkClick={() => history.push("/plants/guild")}
                            accent="#BEC8B6"
                        />
                    </Col>
                </Row>

                {/* ── Row 2: Timeline / Harvest by Area ────────────────────── */}
                <Row className="mb-3 g-3">

                    {/* Plant timeline */}
                    <Col md={7}>
                        <WidgetCard title="Plant Timeline" headerAction={
                            <button onClick={printTimeline} style={{
                                background: "none", border: "none", cursor: "pointer",
                                padding: "0 2px", color: "#4a5e44", fontSize: 15, lineHeight: 1,
                            }} title="Export PDF">
                                <i className="mdi mdi-printer" />
                            </button>
                        }>
                            {timelinePlants.length === 0 ? (
                                <p className="text-muted small mb-0">No annual plants with planting dates found.</p>
                            ) : (
                                <div>
                                    <div style={{ display: "flex", paddingLeft: "30%", marginBottom: 4, paddingRight: "4px" }}>
                                        {MONTHS_TICK.map((m, i) => (
                                            <div key={i} style={{ flex: 1, fontSize: 9, color: "#999", textAlign: "center" }}>
                                                {m}
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ position: "relative", maxHeight: 320, overflowY: "auto" }}>
                                        <div style={{
                                            position: "absolute", left: todayLeft, top: 0, bottom: 0,
                                            width: 1, backgroundColor: "#FF4560", zIndex: 5, pointerEvents: "none",
                                        }} />
                                        {timelinePlants.map((plant, i) => (
                                            <div key={i} style={{ display: "flex", alignItems: "center", marginBottom: 5 }}>
                                                <div style={{
                                                    width: "30%", fontSize: 11, paddingRight: 6,
                                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                    color: "#2f3a34",
                                                }}>
                                                    {plant.species}{plant.variety ? ` (${plant.variety})` : ""}
                                                </div>
                                                <div style={{ width: "70%" }}>
                                                    <TimelineCellRenderer data={plant} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ display: "flex", gap: 14, marginTop: 8, fontSize: 10, color: "#666" }}>
                                        <span><span style={{ display: "inline-block", width: 10, height: 8, backgroundColor: "#00b894", marginRight: 3, borderRadius: 1 }} />Seed → Transplant</span>
                                        <span><span style={{ display: "inline-block", width: 10, height: 8, backgroundColor: "#0984e3", marginRight: 3, borderRadius: 1 }} />Transplant → Harvest</span>
                                        <span><span style={{ display: "inline-block", width: 10, height: 8, backgroundColor: "#FF4560", marginRight: 3, borderRadius: 1 }} />Today</span>
                                    </div>
                                </div>
                            )}
                        </WidgetCard>
                    </Col>

                    {/* Upcoming plants */}
                    <Col md={5}>
                        <WidgetCard title="Upcoming Plants">
                            {upcomingPlantActions.length === 0 ? (
                                <p className="text-muted small mb-0">No plant actions in the next 60 days.</p>
                            ) : (
                                <div style={{ maxHeight: 320, overflowY: "auto" }}>
                                    {upcomingPlantActions.map((p, i) => (
                                        <div key={i} style={{ padding: "5px 0", borderBottom: "1px solid #f0f0f0" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                                <span style={{
                                                    fontSize: 9, padding: "1px 5px", borderRadius: 3,
                                                    backgroundColor: ACTION_COLORS[p.action] || "#ccc",
                                                    color: "#fff", fontWeight: 600, flexShrink: 0,
                                                }}>{p.action}</span>
                                                <span style={{
                                                    fontSize: 12, fontWeight: 500, overflow: "hidden",
                                                    textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#2f3a34",
                                                }}>{p.species}{p.variety ? ` (${p.variety})` : ""}</span>
                                            </div>
                                            <div style={{ fontSize: 11, color: "#6f7a72", marginTop: 1 }}>
                                                {p.dateLabel} · {p.areaName}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </WidgetCard>
                    </Col>
                </Row>

                {/* ── Row 3: Harvest by Area / Season Rotations ──────────── */}
                <Row className="mb-4 g-3">

                    {/* Harvest by area */}
                    <Col md={6}>
                        <WidgetCard title="Harvest by Area">
                            {harvestByArea.length === 0 ? (
                                <p className="text-muted small mb-0">No harvest data logged yet.</p>
                            ) : (
                                <>
                                    <div style={{ maxHeight: 190, overflowY: "auto", marginBottom: 10 }}>
                                        {harvestByArea.map((a, i) => (
                                            <div key={i} style={{ marginBottom: 10 }}>
                                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                                                    <span style={{ fontWeight: 500, color: "#2f3a34" }}>{a.name}</span>
                                                    <span style={{ color: "#6f7a72", fontSize: 11 }}>
                                                        {lbsToDisplay(a.actual)} / {lbsToDisplay(a.estimated)} {harvestUnit}
                                                        <span style={{ marginLeft: 5, color: "#4a5e44", fontWeight: 600 }}>{a.pct}%</span>
                                                    </span>
                                                </div>
                                                <Progress value={a.pct} style={{ height: 7 }}
                                                    color={a.pct >= 100 ? "success" : a.pct >= 50 ? "warning" : "danger"} />
                                            </div>
                                        ))}
                                    </div>
                                    <Chart type="bar" height={130}
                                        series={[{ name: harvestUnit, data: monthlyHarvestTotals }]}
                                        options={{
                                            chart: { toolbar: { show: false } },
                                            dataLabels: { enabled: false },
                                            plotOptions: { bar: { columnWidth: "65%" } },
                                            xaxis: { categories: MONTHS_SHORT, labels: { style: { fontSize: "9px" } } },
                                            yaxis: { labels: { show: false } },
                                            colors: ["#FEB019"],
                                            grid: { show: false },
                                            tooltip: { y: { formatter: val => `${val.toFixed(1)} ${harvestUnit}` } },
                                        }}
                                    />
                                </>
                            )}
                        </WidgetCard>
                    </Col>

                    {/* Season rotations */}
                    <Col md={6}>
                        <WidgetCard title="Season Rotations">
                            {seasonRotations.length === 0 ? (
                                <p className="text-muted small mb-0">No crop rotations assigned yet.</p>
                            ) : (
                                <div style={{ maxHeight: 280, overflowY: "auto" }}>
                                    {seasonRotations.map((areaRot, ai) => (
                                        <div key={ai} style={{ marginBottom: 14 }}>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: "#4a5e44", marginBottom: 6 }}>
                                                {areaRot.areaName}
                                            </div>
                                            <Row style={{ margin: 0 }}>
                                                {SEASONS.map(season => (
                                                    <Col key={season} xs={3} style={{ padding: "0 3px" }}>
                                                        <div style={{ fontSize: 10, fontWeight: 600, color: "#6f7a72", marginBottom: 4 }}>
                                                            {season}
                                                        </div>
                                                        {areaRot.bySeason[season].length === 0
                                                            ? <div style={{ height: 22 }} />
                                                            : areaRot.bySeason[season].map((p, pi) => {
                                                                const ns = NUTRIENT_STYLES[p.nutrient_role] || { background: "#e0e0e0", color: "#424242" };
                                                                return (
                                                                    <div key={pi} style={{
                                                                        fontSize: 10, padding: "2px 5px", borderRadius: 3,
                                                                        marginBottom: 3, overflow: "hidden",
                                                                        textOverflow: "ellipsis", whiteSpace: "nowrap", ...ns,
                                                                    }}>
                                                                        {p.species || p.name}
                                                                    </div>
                                                                );
                                                            })
                                                        }
                                                    </Col>
                                                ))}
                                            </Row>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </WidgetCard>
                    </Col>

                </Row>

            </Container>
        </div>
    );
};

Home.propTypes = {
    resources: PropTypes.array,
};

const mapStateToProps = (state) => ({
    userareas:      state.goals?.areas          || [],
    userGuilds:     state.goals?.userGuilds      || [],
    profile:        state.Profile?.user          || null,
});

const mapDispatchToProps = (dispatch) => ({
    onGetAreas:           ()         => dispatch(getAreas()),
    onGetUserGuilds:      ()         => dispatch(getUserGuilds()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Home));
